import React from 'react';

import queryString from 'query-string';
import Loadable from 'react-loadable';
import { Switch, RouteComponentProps, Route } from 'react-router';

import { AccountLandingRedirect } from './account/components/account-landing-redirect';
import { CheckoutSessionCompletedRedirect } from './account/components/checkout-session-completed-redirect';
import { ApplicantDirectoryRedirect } from './applicant-directory/applicant-directory-redirect';
import { AuthenticationRequiredRoute } from './authentication/authentication-required-route';
import { HelpscoutRedirect } from './helpscout-redirect/helpscout-redirect';
import MainApp from './main-app';
import { OrganizationRoutingPage } from './organization/organization-routing-page';
import { QuickbooksAuthenticationRedirect } from './quickbooks/quickbooks-authentication-redirect';
import { SalesforceAccountLookupRedirect } from './salesforce/salesforce-account-lookup-redirect';
import * as WebRoutes from './web-routes';

import { ADMIN_BASE_ROUTE, PENDING_ADMIN_ACTION } from './admin/util/admin-web-routes';
import { LegacyJobRedirect } from './job/widgets/legacy-job-redirect';
import { HIRING_PERMISSION } from './util/permissions';
import { LoadingMessage } from './widgets/loading-message';
import { NotFoundPage } from './widgets/not-found-page';

/*
Split out the admin and styleguide area and have it load separately
to keep bundle size down for 99% of users.
styleguide is now done in dev-routing
*/
const Admin = Loadable({
  loader: () => import('./admin/admin-page'),
  loading: LoadingMessage,
});

const AccountPage = Loadable({
  loader: () => import('./account/components/account-routing-page'),
  loading: LoadingMessage,
});

const PendingAdminAction = Loadable({
  loader: () => import('./admin/components/pending-admin-action'),
  loading: LoadingMessage,
});

const DevRouting = Loadable({
  loader: () => import('./admin/dev/dev-routing'),
  loading: LoadingMessage,
});

const JobDetailsRouting = Loadable({
  loader: () => import('./job/routing/job-details-routing-page'),
  loading: LoadingMessage,
});

const JobSettingsRouting = Loadable({
  loader: () => import('./job/routing/job-settings-routing-page'),
  loading: LoadingMessage,
});

const JobProcessRouting = Loadable({
  loader: () => import('./job/routing/job-process-routing-page'),
  loading: LoadingMessage,
});

const parseJobRouteParams = (props: RouteComponentProps<OrgAndJobRoute>) => {
  let jobId = null;
  const orgId = parseInt(props.match.params.orgId, 10);
  if (props.match.params.jobId) {
    // This is really funky:
    //  1. This can match /org/:orgId/jobs/detail/:jobId
    //  2. This can _also_ match /org/:orgId/jobs/detail/1
    // As such, jobId here can either be a number or a word. If it's a number, we want to show that job. If it's a word, it's the
    // route for the org page, which, the routing is handled in the job page below. So we force the jobId to be null here if it's NaN
    jobId = parseInt(props.match.params.jobId, 10) ?? null;
  }

  return { orgId, jobId };
};

export const AppRouting = () => (
  <Switch>
    <AuthenticationRequiredRoute
      path={WebRoutes.applicantDirectoryRoute()}
      render={() => <ApplicantDirectoryRedirect />}
    />
    <AuthenticationRequiredRoute requiresAdmin path={ADMIN_BASE_ROUTE} render={() => <Admin />} />
    <AuthenticationRequiredRoute
      requiresAdmin
      path={PENDING_ADMIN_ACTION()}
      render={() => <PendingAdminAction />}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.ACCOUNT_BILLING_CHECKOUT_REDIRECT}
      render={(props: RouteComponentProps) => (
        <CheckoutSessionCompletedRedirect queryParams={queryString.parse(props.location.search)} />
      )}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.directJobPageRoute()}
      render={(props: RouteComponentProps<JobRouteProps>) => (
        <LegacyJobRedirect jobId={parseInt(props.match.params.jobId, 10)} />
      )}
    />
    {/* Route order matters here - JobProcessRoutes must be registered last */}
    <AuthenticationRequiredRoute
      path={[WebRoutes.jobDetailsBaseRoute(), WebRoutes.orgJobDetailsRoute()]}
      requiredHiringPermission={HIRING_PERMISSION.ALL}
      render={(props: RouteComponentProps<OrgAndJobRoute>) => {
        const { orgId, jobId } = parseJobRouteParams(props);
        if (!orgId) {
          return <NotFoundPage />;
        }

        return (
          <JobDetailsRouting
            orgId={orgId}
            jobId={jobId}
            queryParams={queryString.parse(props.location.search)}
          />
        );
      }}
    />
    <AuthenticationRequiredRoute
      path={[WebRoutes.jobSettingsBaseRoute(), WebRoutes.orgJobSettingsRoute()]}
      requiredHiringPermission={HIRING_PERMISSION.ALL}
      render={(props: RouteComponentProps<OrgAndJobRoute>) => {
        const { orgId, jobId } = parseJobRouteParams(props);
        if (!orgId) {
          return <NotFoundPage />;
        }
        return (
          <JobSettingsRouting
            orgId={orgId}
            jobId={jobId}
            queryParams={queryString.parse(props.location.search)}
          />
        );
      }}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.jobPageRoute()}
      requiredHiringPermission={HIRING_PERMISSION.ALL}
      render={(props: RouteComponentProps<OrgAndJobRoute>) => {
        const { orgId, jobId } = parseJobRouteParams(props);
        if (!orgId) {
          return <NotFoundPage />;
        }
        return (
          <JobProcessRouting
            orgId={orgId}
            jobId={jobId}
            queryParams={queryString.parse(props.location.search)}
          />
        );
      }}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.orgRoute()}
      render={(props: RouteComponentProps<OrgRouteProps>) => (
        <OrganizationRoutingPage
          orgId={parseInt(props.match.params.orgId, 10)}
          queryParams={queryString.parse(props.location.search)}
        />
      )}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.quickbooksAuthenticationRoute()}
      render={(props: RouteComponentProps) => (
        <QuickbooksAuthenticationRedirect queryParams={queryString.parse(props.location.search)} />
      )}
    />
    <AuthenticationRequiredRoute requiresAdmin path="/dev" component={DevRouting} />
    <AuthenticationRequiredRoute
      requiresAdmin
      path={WebRoutes.salesforceAccountLookupRoute()}
      render={(props: RouteComponentProps) => (
        <SalesforceAccountLookupRedirect queryParams={queryString.parse(props.location.search)} />
      )}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.accountBaseRoute()}
      render={() => <AccountLandingRedirect />}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.accountRoute()}
      render={(props: RouteComponentProps) => (
        <AccountPage queryParams={queryString.parse(props.location.search)} />
      )}
    />
    <AuthenticationRequiredRoute
      path={WebRoutes.HELPSCOUT_REDIRECT_DEV_ROUTE}
      render={() => <HelpscoutRedirect />}
    />
    <Route render={(props) => <MainApp {...props} />} />
  </Switch>
);
